import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CONFIG from '../../config/config';
import InputNumber from '../InputNumber/InputNumber';
import { getDefaultCurrency } from '../../utils/currencyDetection';
import { Settings } from '../../store/reducers/AccountSlice';

interface PriceFieldProps {
  label: string;
  currency?: string | null; // Made optional to support smart defaults
  price: number;
  currencies: string[];
  onCurrencyChange: (currency: string) => void;
  onPriceChange: (price: number) => void;
  isDisabled?: boolean;
}

const PriceField: React.FC<PriceFieldProps> = ({
  label,
  currency: currencyProp,
  price,
  currencies,
  onCurrencyChange,
  onPriceChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  
  const defaultCurrency = useMemo(() => {
    return getDefaultCurrency(
      currencyProp,
      settings?.currency,
      settings?.isLoggedIn
    );
  }, [currencyProp, settings?.currency, settings?.isLoggedIn]);
  
  const [internalCurrency, setInternalCurrency] = useState<string>(() => {
    if (currencyProp && currencyProp.trim() !== '' && CONFIG.SUPPORTED_CURRENCIES.includes(currencyProp)) {
      return currencyProp;
    }
    return getDefaultCurrency(undefined, settings?.currency, settings?.isLoggedIn);
  });
  
  const [userHasChangedCurrency, setUserHasChangedCurrency] = useState(false);

  useEffect(() => {
    if (currencyProp && currencyProp.trim() !== '' && CONFIG.SUPPORTED_CURRENCIES.includes(currencyProp)) {
      setInternalCurrency(currencyProp);
      setUserHasChangedCurrency(false);
    } else if ((!currencyProp || currencyProp.trim() === '') && !userHasChangedCurrency) {
      setInternalCurrency(defaultCurrency);
    }
  }, [currencyProp, defaultCurrency, userHasChangedCurrency]);

  // Keep parent form state in sync with the currency shown in the UI. Otherwise new items
  // keep currency "" while the select displays a smart default (e.g. PLN), and the API
  // falls back to a server default (e.g. EUR) on save.
  const propIsValidCurrency =
    !!(currencyProp && currencyProp.trim() !== '' && CONFIG.SUPPORTED_CURRENCIES.includes(currencyProp));
  useEffect(() => {
    if (isDisabled || userHasChangedCurrency || propIsValidCurrency) {
      return;
    }
    onCurrencyChange(defaultCurrency);
  }, [defaultCurrency, isDisabled, onCurrencyChange, propIsValidCurrency, userHasChangedCurrency]);
  
  const displayCurrency = (currencyProp && currencyProp.trim() !== '' && CONFIG.SUPPORTED_CURRENCIES.includes(currencyProp))
    ? currencyProp
    : internalCurrency;
  
  const handleCurrencyChange = (newCurrency: string) => {
    setInternalCurrency(newCurrency);
    setUserHasChangedCurrency(true); // Mark that user has manually changed the currency
    onCurrencyChange(newCurrency);
  };

  return (
    <div className="flex-1 w-full">
      { label && <p className="text-sm w-full font-extrabold">{ t(label) }:</p> }
      <div className="relative rounded-md">
        <InputNumber
            validationFn={(value) => !!value && value >= 0}
            onChange={e => onPriceChange(Number(e.target.value))}
            defaultValue={price}
            isDisabled={isDisabled}
            errorMessage={"errors.minimum_zero"}
            symbol={CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[displayCurrency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS]}
        />
        <div className="absolute inset-y-0 right-0 flex items-center">
          <select
            value={displayCurrency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            className="rounded-md border-0 bg-transparent px-1 pt-3 pb-3 w-full text-gray-500 focus:outline-none focus:ring-0 sm:text-sm"
            disabled={isDisabled}
          >
            {currencies.map((curr) => (
              <option key={curr} value={curr}>
                {curr}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default PriceField;